const LAST_ACTIVITY_KEY = 'sessionTimeoutLastActivity'
const FORCE_LOGOUT_KEY = 'sessionTimeoutForceLogout'
const EXPIRED_FLAG_KEY = 'sessionTimeoutExpired'

export interface SessionTimeoutConfig {
  enabled: boolean
  timeoutMs: number
  warningMs: number
}

type RemainingListener = (remainingMs: number) => void
type ExpirePromptListener = (active: boolean) => void
type ExpireHandler = () => void

const ACTIVITY_EVENTS = [
  'mousedown',
  'mousemove',
  'keydown',
  'scroll',
  'touchstart',
  'click',
  'wheel'
]

const ACTIVITY_THROTTLE_MS = 1000
const TICK_INTERVAL_MS = 1000
const BACKEND_SYNC_MIN_MS = 8000
const BACKEND_SYNC_MAX_MS = 15000

function parseBooleanEnv(value: string | undefined, fallback = false): boolean {
  if (value === undefined || value === '') return fallback
  return ['true', '1', 'yes'].includes(String(value).toLowerCase())
}

function parsePositiveNumber(value: string | undefined, fallback: number): number {
  const parsed = Number(value)
  return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback
}

export function getSessionTimeoutConfig(): SessionTimeoutConfig {
  const enabled = parseBooleanEnv(process.env.VUE_APP_SESSION_TIMEOUT_ENABLED, false)
  const minutes = parsePositiveNumber(process.env.VUE_APP_SESSION_TIMEOUT_MINUTES, 30)
  const warningSeconds = parsePositiveNumber(process.env.VUE_APP_SESSION_TIMEOUT_WARNING_SECONDS, 120)
  const timeoutMs = minutes * 60 * 1000

  return {
    enabled,
    timeoutMs,
    warningMs: Math.min(warningSeconds * 1000, timeoutMs)
  }
}

class SessionTimeoutService {
  private started = false
  private loggingOut = false
  private warningLocked = false
  private expiredPrompt = false
  private redirectingToLogin = false
  private tickTimer: ReturnType<typeof setInterval> | null = null
  private lastWriteAt = 0
  private lastBackendSyncAt = 0
  private backendSyncInFlight = false
  private expireHandler: ExpireHandler | null = null
  private subscribers: RemainingListener[] = []
  private expirePromptListeners: ExpirePromptListener[] = []
  private boundOnActivity = this.onActivity.bind(this)
  private boundOnStorage = this.onStorage.bind(this)
  private boundOnResume = this.onResume.bind(this)

  getConfig(): SessionTimeoutConfig {
    return getSessionTimeoutConfig()
  }

  isEnabled(): boolean {
    return this.getConfig().enabled
  }

  onExpire(handler: ExpireHandler): void {
    this.expireHandler = handler
  }

  subscribe(listener: RemainingListener): () => void {
    this.subscribers.push(listener)
    listener(this.getRemainingMs())

    return () => {
      this.subscribers = this.subscribers.filter(item => item !== listener)
    }
  }

  subscribeExpirePrompt(listener: ExpirePromptListener): () => void {
    this.expirePromptListeners.push(listener)
    listener(this.expiredPrompt)

    return () => {
      this.expirePromptListeners = this.expirePromptListeners.filter(item => item !== listener)
    }
  }

  isExpiredPromptActive(): boolean {
    return this.expiredPrompt
  }

  isRedirectingToLogin(): boolean {
    return this.redirectingToLogin
  }

  showExpiredPrompt(): void {
    if (this.redirectingToLogin) return
    this.expire()
  }

  beginLogoutToLogin(): void {
    this.redirectingToLogin = true
    this.resetExpiredState()
    this.stop()
    this.clearLocalAuth()
  }

  private clearLocalAuth(): void {
    try {
      require('@/auth/authService').default.logOut()
    } catch (error) {
      localStorage.removeItem('userInfo')
      localStorage.removeItem('loggedIn')
    }

    try {
      require('@/store/store').default.dispatch('resetUserInfo')
    } catch (error) {
      // O store pode ainda não estar pronto no boot.
    }
  }

  resetExpiredState(): void {
    this.loggingOut = false
    localStorage.removeItem(FORCE_LOGOUT_KEY)
    sessionStorage.removeItem(EXPIRED_FLAG_KEY)
    this.setExpiredPrompt(false)
  }

  start(): void {
    if (this.started || !this.isEnabled()) return

    this.redirectingToLogin = false
    this.resetExpiredState()
    this.started = true
    this.warningLocked = false
    this.lastBackendSyncAt = 0
    this.backendSyncInFlight = false
    this.resetActivity(true)
    this.syncBackendSession(true)
    this.bindListeners()
    this.tick()
    this.tickTimer = setInterval(() => this.tick(), TICK_INTERVAL_MS)
  }

  stop(): void {
    this.started = false
    this.warningLocked = false
    this.unbindListeners()

    if (this.tickTimer) {
      clearInterval(this.tickTimer)
      this.tickTimer = null
    }
  }

  resetActivity(force = false): void {
    const now = Date.now()
    if (!force && now - this.lastWriteAt < ACTIVITY_THROTTLE_MS) return

    this.lastWriteAt = now
    localStorage.removeItem(FORCE_LOGOUT_KEY)
    localStorage.setItem(LAST_ACTIVITY_KEY, String(now))
    if (force) {
      this.warningLocked = false
    }
    this.syncWarningLock()
    this.emit()
  }

  renew(): void {
    if (!this.started || this.loggingOut) return
    this.warningLocked = false
    this.resetActivity(true)
    this.syncBackendSession(true)
  }

  isWarningActive(): boolean {
    return this.warningLocked && this.getRemainingMs() > 0
  }

  clearActivity(): void {
    localStorage.removeItem(LAST_ACTIVITY_KEY)
    this.lastWriteAt = 0
    this.emit()
  }

  getRemainingMs(): number {
    if (!this.isEnabled()) return 0
    if (localStorage.getItem(FORCE_LOGOUT_KEY)) return 0

    const { timeoutMs } = this.getConfig()
    const lastActivity = this.readLastActivity()
    if (!lastActivity) {
      return this.started ? 0 : timeoutMs
    }

    return Math.max(0, timeoutMs - (Date.now() - lastActivity))
  }

  markExpired(): void {
    sessionStorage.setItem(EXPIRED_FLAG_KEY, '1')
  }

  consumeExpiredFlag(): boolean {
    const expired = sessionStorage.getItem(EXPIRED_FLAG_KEY) === '1'
    if (expired) sessionStorage.removeItem(EXPIRED_FLAG_KEY)
    return expired
  }

  private readLastActivity(): number {
    const raw = localStorage.getItem(LAST_ACTIVITY_KEY)
    const parsed = Number(raw)
    return Number.isFinite(parsed) && parsed > 0 ? parsed : 0
  }

  private onActivity(event?: Event): void {
    if (!this.started || this.loggingOut || this.warningLocked) return
    if (!this.assertStillValid()) return
    this.acknowledgeActivity()
  }

  notifyApiActivity(url: string = ''): void {
    if (!this.started || this.loggingOut || this.warningLocked) return
    if (this.isKeepAliveUrl(url)) return
    if (!this.assertStillValid()) return
    this.acknowledgeActivity()
  }

  onUserNavigation(): void {
    if (!this.started || this.loggingOut || this.warningLocked) return
    if (!this.assertStillValid()) return
    this.acknowledgeActivity()
  }

  private acknowledgeActivity(): void {
    this.resetActivity()
    this.syncBackendSession()
  }

  private onResume(): void {
    if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return
    this.tick()
  }

  private assertStillValid(): boolean {
    if (this.getRemainingMs() > 0) return true
    this.expire()
    return false
  }

  private isKeepAliveUrl(url: string): boolean {
    return url.includes('/ping') || url.includes('/logged') || url.includes('/session/continue')
  }

  private getBackendSyncThrottleMs(): number {
    const { timeoutMs } = this.getConfig()
    return Math.min(BACKEND_SYNC_MAX_MS, Math.max(BACKEND_SYNC_MIN_MS, Math.floor(timeoutMs / 4)))
  }

  private syncBackendSession(force = false): void {
    if (!this.started || this.loggingOut || this.warningLocked) return
    if (this.backendSyncInFlight && !force) return

    const now = Date.now()
    if (!force && now - this.lastBackendSyncAt < this.getBackendSyncThrottleMs()) return

    this.lastBackendSyncAt = now
    this.backendSyncInFlight = true

    try {
      const LoginService = require('@/services/api/LoginService').default
      LoginService.build()
        .post('v1/session/continue', {})
        .catch(() => {
          // 401 de timeout é tratado no interceptor padrão.
        })
        .finally(() => {
          this.backendSyncInFlight = false
        })
    } catch (error) {
      this.backendSyncInFlight = false
    }
  }

  private onStorage(event: StorageEvent): void {
    if (event.key === FORCE_LOGOUT_KEY && event.newValue) {
      this.expire()
      return
    }

    if (event.key === LAST_ACTIVITY_KEY) {
      this.syncWarningLock()
      this.emit()
    }
  }

  private tick(): void {
    if (!this.started || this.loggingOut) return

    this.syncWarningLock()
    const remaining = this.getRemainingMs()
    this.emit()

    if (remaining <= 0) {
      this.expire()
    }
  }

  private syncWarningLock(): void {
    const remaining = this.getRemainingMs()
    const { warningMs } = this.getConfig()

    if (remaining > 0 && remaining <= warningMs) {
      this.warningLocked = true
      return
    }

    if (remaining > warningMs) {
      this.warningLocked = false
    }
  }

  private expire(): void {
    if (this.redirectingToLogin) return

    if (this.expiredPrompt) {
      this.setExpiredPrompt(true)
      return
    }

    this.loggingOut = true
    this.markExpired()
    localStorage.setItem(FORCE_LOGOUT_KEY, String(Date.now()))
    localStorage.removeItem(LAST_ACTIVITY_KEY)
    this.stop()
    this.setExpiredPrompt(true)

    if (this.expireHandler) {
      this.expireHandler()
    }
  }

  private setExpiredPrompt(active: boolean): void {
    this.expiredPrompt = active
    this.expirePromptListeners.forEach(listener => listener(active))
  }

  private emit(): void {
    const remaining = this.getRemainingMs()
    this.subscribers.forEach(listener => listener(remaining))
  }

  private bindListeners(): void {
    ACTIVITY_EVENTS.forEach(eventName => {
      window.addEventListener(eventName, this.boundOnActivity, { capture: true, passive: true })
    })
    window.addEventListener('storage', this.boundOnStorage)
    document.addEventListener('visibilitychange', this.boundOnResume)
    window.addEventListener('focus', this.boundOnResume)
    window.addEventListener('pageshow', this.boundOnResume)
  }

  private unbindListeners(): void {
    ACTIVITY_EVENTS.forEach(eventName => {
      window.removeEventListener(eventName, this.boundOnActivity, true)
    })
    window.removeEventListener('storage', this.boundOnStorage)
    document.removeEventListener('visibilitychange', this.boundOnResume)
    window.removeEventListener('focus', this.boundOnResume)
    window.removeEventListener('pageshow', this.boundOnResume)
  }
}

const sessionTimeoutService = new SessionTimeoutService()

export default sessionTimeoutService
